import React from 'react';

import { useMatchMedia } from '@betterment/js-runtime';
import { Box, Card, Heading, Icon, Text } from '@betterment/samba-react';
import { css } from '@betterment/samba-styled-system/css';
import { CardLoader, SambaAutoLink } from '@betterment/shared-components';

import { addAccountUrl } from '../../Shared/addAccountUrl';
import { urlBuilder } from '../../Shared/urlBuilder';
import { HomeProductOnboardingOptionsQuery } from '../graphql/homeProductOnboardingOptions.generated';
import { useProductOnboardingOptions } from '../hooks/useProductOnboardingOptions';

type OptionCardProps = {
  option: HomeProductOnboardingOptionsQuery['productOnboardingOptions'][number];
  marginTop?: 'space.200';
};

const OptionCard: React.FC<OptionCardProps> = ({ option, marginTop }) => {
  const { name, legacyProductCategory, steps } = option;

  const hasSteps = !!steps?.length;
  const flexProps = hasSteps
    ? { flexDirection: 'column', alignItems: 'start', justifyContent: 'start' }
    : { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' };

  const href = () => {
    if (option.__typename === 'PremiumOnboardingOption') return urlBuilder.appAdviceInitiationFlow.absolute();

    if (option.__typename === 'Solo401kOnboardingOption')
      return urlBuilder.appAdviceInitiationFlow.absolute({
        signup_intent: legacyProductCategory,
        product_category: legacyProductCategory,
      });

    return addAccountUrl({ product_category: legacyProductCategory });
  };

  const isSmallScreen = useMatchMedia('atLeastMedium');
  const gridColumnStart = isSmallScreen ? 1 : 2;

  return (
    <Card
      css={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: 'space.300',
        marginTop,
        gridColumnStart,
      }}
    >
      <Box css={{ display: 'flex', ...flexProps }}>
        <Box css={{ display: 'flex', alignItems: 'center', flexDirection: 'row' }}>
          <img
            className={css({ height: 'icon.medium', width: 'icon.medium', marginRight: 'space.150' })}
            src={option.image.light}
            alt={`${name} icon`}
          />
          <Heading variant="title200">{name}</Heading>
        </Box>
        {hasSteps && (
          <Box css={{ display: 'flex', flexDirection: 'column', marginY: 'space.300', gap: 'space.100' }}>
            {steps.map(step => (
              <Box css={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }} key={step.id}>
                <Icon
                  name={step.state === 'FINISHED' ? 'CheckmarkCircleFilled' : 'CheckmarkCircle'}
                  size="small"
                  color={step.state === 'FINISHED' ? 'icon' : 'icon.weak'}
                  css={{ marginRight: 'space.100' }}
                />
                <Text css={{ textDecoration: step.state === 'FINISHED' ? 'line-through' : 'none' }}>
                  {step.shortDescription}
                </Text>
              </Box>
            ))}
          </Box>
        )}

        <SambaAutoLink to={href()} variant="solidPrimary" spacing="default" css={{ alignSelf: 'start' }}>
          Finish setup
        </SambaAutoLink>
      </Box>
    </Card>
  );
};

export const ProductOnboardingInProgressCard: React.FC = () => {
  const { data } = useProductOnboardingOptions();

  if (!data) return <CardLoader />;

  if (!data.inProgressOptions.length) return null;

  return (
    <>
      {data.inProgressOptions.map((option, i) => (
        <OptionCard option={option} marginTop={i > 0 ? 'space.200' : undefined} key={option.id} />
      ))}
    </>
  );
};
